import React, { useCallback, useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useNavigate } from "react-router-dom";
import { ChevronRight } from "lucide-react";
import { products } from "../../products";

// Fallback Images
const placeholderImages = [
  "https://via.placeholder.com/600x600.png?text=Product+1",
  "https://via.placeholder.com/600x600.png?text=Product+2",
  "https://via.placeholder.com/600x600.png?text=Product+3",
];

// Theme Colors
const gold = "#DBAE6E";
const darkGold = "#B58B4C";

function ProductSection() {
  const navigate = useNavigate();
  const [currentProductIndex, setCurrentProductIndex] = useState(0);
  const [typedText, setTypedText] = useState("");
  const [isTyping, setIsTyping] = useState(true);

  const defaultProduct = {
    id: "1",
    name: "Product",
    price: 999,
    discount: 0.2,
    sizes: ["S", "M", "L"],
    images: { main: [placeholderImages[0]] },
    description: "Experience unmatched quality and style with our featured product.",
  };

  const productList = products.length > 0 ? products : [defaultProduct];
  const featuredProduct = productList[currentProductIndex];

  const handleProductClick = useCallback(() => {
    navigate(`/product/${featuredProduct.id}`);
  }, [navigate, featuredProduct.id]);

  useEffect(() => {
    let typingTimeout, backspacingTimeout;
    const currentName = featuredProduct.name;

    if (isTyping) {
      if (typedText.length < currentName.length) {
        typingTimeout = setTimeout(() => {
          setTypedText(currentName.slice(0, typedText.length + 1));
        }, 100);
      } else {
        setTimeout(() => setIsTyping(false), 2000);
      }
    } else {
      if (typedText.length > 0) {
        backspacingTimeout = setTimeout(() => {
          setTypedText(typedText.slice(0, -1));
        }, 50);
      } else {
        setCurrentProductIndex((prev) => (prev + 1) % productList.length);
        setIsTyping(true);
      }
    }

    return () => {
      clearTimeout(typingTimeout);
      clearTimeout(backspacingTimeout);
    };
  }, [typedText, isTyping, featuredProduct.name, productList.length]);

  const heroVariants = {
    hidden: { opacity: 0, y: 30, filter: "blur(4px)" },
    visible: { opacity: 1, y: 0, filter: "blur(0px)", transition: { duration: 0.6, ease: "easeOut" } },
    exit: { opacity: 0, y: -30, filter: "blur(4px)", transition: { duration: 0.4, ease: "easeIn" } },
  };

  const imageVariants = {
    hidden: { opacity: 0, scale: 0.9, filter: "blur(4px)" },
    visible: { opacity: 1, scale: 1, filter: "blur(0px)", transition: { duration: 0.8, ease: "easeOut", delay: 0.2 } },
    exit: { opacity: 0, scale: 0.9, filter: "blur(4px)", transition: { duration: 0.4, ease: "easeIn" } },
    hover: {
      scale: 1.05,
      filter: `drop-shadow(0 0 20px ${gold}66)`,
      transition: { duration: 0.4 },
    },
  };

  const buttonVariants = {
    hidden: { opacity: 0, x: -20 },
    visible: { opacity: 1, x: 0, transition: { duration: 0.5, ease: "easeOut", delay: 0.6 } },
    exit: { opacity: 0, x: -20, transition: { duration: 0.3, ease: "easeIn" } },
    hover: {
      scale: 1.05,
      boxShadow: `0 0 15px ${gold}66`,
      background: `linear-gradient(45deg, ${gold}, ${darkGold})`,
      transition: { duration: 0.3 },
    },
    tap: { scale: 0.95 },
  };

  const textVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: (i) => ({
      opacity: 1,
      y: 0,
      transition: { duration: 0.1, ease: "easeOut", delay: i * 0.2 + 0.4 },
    }),
    exit: { opacity: 0, y: 20, transition: { duration: 0.3, ease: "easeIn" } },
  };

  return (
    <section
      className="w-full bg-white dark:bg-black text-black dark:text-[#DBAE6E] py-12 sm:py-16 overflow-hidden relative"
      aria-label="Featured Product Hero Section"
    >
      {/* Background Particles */}
      {[...Array(20)].map((_, i) => (
        <motion.div
          key={i}
          className="absolute rounded-full pointer-events-none"
          style={{
            width: `${Math.random() * 2 + 1}px`,
            height: `${Math.random() * 2 + 1}px`,
            backgroundColor: gold,
            filter: `blur(1.5px)`,
          }}
          initial={{ x: `${Math.random() * 100}%`, y: "100vh", opacity: 0 }}
          animate={{ y: "-10vh", opacity: [0, 0.3, 0] }}
          transition={{
            duration: Math.random() * 5 + 3,
            repeat: Infinity,
            delay: Math.random() * 3,
          }}
        />
      ))}

      {/* Gradient Overlay */}
      <div
        className="absolute inset-0 pointer-events-none"
        style={{
          background: `radial-gradient(circle at center, ${gold}22 0%, transparent 70%)`,
          opacity: 0.15,
        }}
      />

      <div className="container mx-auto px-4 sm:px-6 lg:px-8 flex flex-col items-center min-h-[80vh] justify-center relative z-10">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 items-center w-full max-w-7xl">
          {/* Text Content */}
          <AnimatePresence mode="wait">
            <motion.div
              key={currentProductIndex}
              className="flex flex-col items-start space-y-6"
              variants={heroVariants}
              initial="hidden"
              animate="visible"
              exit="exit"
            >
              <h2 className="text-4xl sm:text-5xl lg:text-6xl font-extrabold uppercase tracking-tight">
                {typedText}
                <motion.span
                  animate={{ opacity: [1, 0, 1] }}
                  transition={{ duration: 0.5, repeat: Infinity }}
                  className="inline-block w-1 h-8 sm:h-10 ml-1 bg-[#DBAE6E]"
                />
              </h2>
              <motion.p
                className="text-lg sm:text-xl font-light max-w-md"
                variants={textVariants}
                custom={0}
                initial="hidden"
                animate="visible"
                exit="exit"
              >
                {featuredProduct.description}
              </motion.p>
              <motion.div
                className="flex items-center gap-4"
                variants={textVariants}
                custom={1}
                initial="hidden"
                animate="visible"
                exit="exit"
              >
                <span className="text-2xl font-semibold">
                  ₹{(featuredProduct.price * (1 - featuredProduct.discount)).toFixed(0)}
                </span>
                {featuredProduct.discount > 0 && (
                  <span className="text-gray-500 line-through text-lg">
                    ₹{featuredProduct.price}
                  </span>
                )}
              </motion.div>
              <motion.div
                className="flex flex-wrap gap-2"
                variants={textVariants}
                custom={2}
                initial="hidden"
                animate="visible"
                exit="exit"
              >
                {featuredProduct.sizes.map((size) => (
                  <span
                    key={size}
                    className="text-sm px-3 py-1 rounded-full border"
                    style={{ color: gold, borderColor: gold }}
                  >
                    {size}
                  </span>
                ))}
              </motion.div>
              <motion.button
                className="mt-4 px-8 py-3 bg-gradient-to-r from-[#DBAE6E] to-[#B58B4C] text-black font-semibold rounded-full flex items-center gap-2"
                variants={buttonVariants}
                initial="hidden"
                animate="visible"
                exit="exit"
                whileHover="hover"
                whileTap="tap"
                onClick={handleProductClick}
              >
                Shop Now <ChevronRight size={20} />
              </motion.button>
            </motion.div>
          </AnimatePresence>

          {/* Image Content */}
          <AnimatePresence mode="wait">
            <motion.div
              key={currentProductIndex}
              className="relative flex justify-center"
              variants={imageVariants}
              initial="hidden"
              animate="visible"
              exit="exit"
              whileHover="hover"
            >
              <div
                className="relative w-full max-w-md h-[400px] sm:h-[500px] rounded-2xl overflow-hidden border"
                style={{
                  borderColor: gold,
                  background: "black",
                  boxShadow: `0 0 30px ${gold}44`,
                }}
              >
                <img
                  src={
                    featuredProduct.images?.[Object.keys(featuredProduct.images)[0]]?.[0] ||
                    placeholderImages[0]
                  }
                  alt={featuredProduct.name}
                  className="w-full h-full object-cover"
                  loading="lazy"
                />
                {featuredProduct.discount > 0 && (
                  <span
                    className="absolute top-4 right-4 bg-gradient-to-br from-[#DBAE6E] to-[#B58B4C] text-black text-sm font-bold px-3 py-1 rounded-full"
                    style={{ boxShadow: `0 0 10px ${gold}66` }}
                  >
                    {Math.round(featuredProduct.discount * 100)}% OFF
                  </span>
                )}
              </div>
              <motion.div
                className="absolute -top-4 -left-4 w-16 h-16 rounded-full"
                style={{ background: `${gold}33`, filter: "blur(10px)" }}
                animate={{ scale: [1, 1.2, 1], opacity: [0.5, 0.8, 0.5] }}
                transition={{ duration: 3, repeat: Infinity }}
              />
              <motion.div
                className="absolute -bottom-4 -right-4 w-20 h-20 rounded-full"
                style={{ background: `${darkGold}33`, filter: "blur(12px)" }}
                animate={{ scale: [1, 1.3, 1], opacity: [0.5, 0.7, 0.5] }}
                transition={{ duration: 4, repeat: Infinity }}
              />
            </motion.div>
          </AnimatePresence>
        </div>
      </div>
    </section>
  );
}

export default ProductSection;
